import { Box, Paper, Chip, Typography } from '@mui/material'
import { StructuredQuestion, QUESTION_TYPES } from '../interfaces/questions'

interface QuestionDisplayProps {
  question: StructuredQuestion
  highlightFields?: string[]
}

const getQuestionTypeLabel = (type: string): string => {
  const questionType = QUESTION_TYPES.find((qt) => qt.value === type)
  return questionType?.label || type
}

export default function QuestionDisplay({
  question,
  highlightFields,
}: QuestionDisplayProps) {
  const hl = (field: string) =>
    highlightFields?.includes(field)
      ? { bgcolor: 'warning.50', borderRadius: 1, px: 0.5 }
      : {}

  return (
    <Paper sx={{ p: 2, mb: 2, bgcolor: '#fafafa', overflow: 'hidden', wordBreak: 'break-word' }}>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
        <Chip
          label={question.question_number}
          size="small"
          color="primary"
          variant="outlined"
        />
        <Box component="span" sx={hl('question_type')}>
          <Chip label={getQuestionTypeLabel(question.question_type)} size="small" />
        </Box>
        {question.section && (
          <Box component="span" sx={hl('section')}>
            <Chip
              label={`Section: ${question.section}`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          </Box>
        )}
      </Box>
      <Box sx={hl('question')}>
        <Typography variant="body1" fontWeight="500" sx={{ mb: 1 }}>
          {question.question}
        </Typography>
      </Box>
      {question.question_details && (
        <Box sx={hl('question_details')}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {question.question_details}
          </Typography>
        </Box>
      )}
      {question.options && question.options.length > 0 && (
        <Box sx={{ mt: 1, ...hl('options') }}>
          <Typography variant="caption" fontWeight="500">
            Options:
          </Typography>
          <Box sx={{ ml: 2 }}>
            {question.options.map((opt, idx) => (
              <Typography key={idx} variant="body2">
                • {opt}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        {question.required && (
          <Box component="span" sx={hl('required')}>
            <Chip label="Required" size="small" color="error" />
          </Box>
        )}
        {question.attachment_required && (
          <Box component="span" sx={hl('attachment_required')}>
            <Chip label="Attachment Required" size="small" color="warning" />
          </Box>
        )}
        {question.char_limit && (
          <Box component="span" sx={hl('char_limit')}>
            <Chip
              label={`Max ${question.char_limit} chars`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
        {question.word_limit && (
          <Box component="span" sx={hl('word_limit')}>
            <Chip
              label={`Max ${question.word_limit} words`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
        {question.conditional_on && (
          <Chip
            label={`Conditional on: Question ${question.conditional_on.question_number} ${question.conditional_on.operator} ${question.conditional_on.value}`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {question.depends_on && question.depends_on.question_numbers && (
          <Chip
            label={`Depends on: Questions ${question.depends_on.question_numbers.join(
              ', '
            )}`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
        {question.has_other_text_field && (
          <Chip
            label={`Text input enabled for 'Other' option`}
            size="small"
            variant="outlined"
          />
        )}
      </Box>
    </Paper>
  )
}
